import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NavigationDropdown, NavigationItem, NavigationLink } from '../../../interfaces/navigation-item.interface';
import { dropdownAnimation } from '../../../animations/dropdown.animation';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { NavigationService } from '../../../services/navigation.service';
import icKeyboardArrowRight from '@iconify/icons-ic/twotone-keyboard-arrow-right';
import { MatDialog } from '@angular/material/dialog';
import { JobCreateUpdateComponent } from 'src/app/pages/apps/jobs/jobs-listing/job-create-update/job-create-update.component';
import { JobApprovalCreateUpdateComponent } from 'src/app/pages/apps/jobs/jobs-approval-listing/job-approval-create-update/job-approval-create-update.component';
import { JobsService } from 'src/@vex/services/jobsService/jobs.service';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionComponent } from 'src/app/pages/apps/settings/company-setting/memberships/subscription/subscription.component';
import { PreferenceKeys } from 'src/app/interfaces/preference-keys.enum';
import { SubscriptionProduct } from 'src/app/pages/pages/pricing/interface/subscription-product';
import { VideoTutorialComponent } from 'src/app/components/video-tutorial/video-tutorial.component';
import { Tutorials } from 'src/app/interfaces/tutorials.enum';


@UntilDestroy()
@Component({
  selector: 'vex-sidenav-item',
  templateUrl: './sidenav-item.component.html',
  styleUrls: ['./sidenav-item.component.scss'],
  animations: [dropdownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavItemComponent implements OnInit, OnChanges {

  @Input() item: NavigationItem;
  @Input() level: number;

  isOpen: boolean;
  isActive: boolean;
  icKeyboardArrowRight = icKeyboardArrowRight;
  items = this.navigationService.items;

  isLink = this.navigationService.isLink;
  isDropdown = this.navigationService.isDropdown;
  isSubheading = this.navigationService.isSubheading;
  isButton = this.navigationService.isButton;

  userRole: number;

  constructor(private router: Router,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private navigationService: NavigationService, 
    private jobService: JobsService,
    private translate: TranslateService) {
    const user = JSON.parse(localStorage.getItem('UserInfo'));
    this.userRole = user.role;
  }

  @HostBinding('class')
  get levelClass() {
    return `item-level-${this.level}`;
  }

  ngOnInit() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      filter(() => this.isDropdown(this.item)),
      untilDestroyed(this)
    ).subscribe(() => this.onRouteChange());

    this.navigationService.openChange$.pipe(
      filter(() => this.isDropdown(this.item)),
      untilDestroyed(this)
    ).subscribe(item => this.onOpenChange(item));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.hasOwnProperty('item') && this.isDropdown(this.item)) {
      this.onRouteChange();
    }
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
    this.cd.markForCheck();
  }

  onOpenChange(item: NavigationDropdown) {
    if (this.isChildrenOf(this.item as NavigationDropdown, item)) {
      return;
    }

    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      return;
    }

    if (this.item !== item) {
      this.isOpen = false;
      this.cd.markForCheck();
    }
  }

  onRouteChange() {
    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      this.isActive = true;
      this.isOpen = true;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    } else {
      this.isActive = false;
      this.isOpen = false;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    }
  }

  isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown) {
    if (parent.children.indexOf(item) !== -1) {
      return true;
    }

    return parent.children
      .filter(child => this.isDropdown(child))
      .some(child => this.isChildrenOf(child as NavigationDropdown, item));
  }

  hasActiveChilds(parent: NavigationDropdown) {
    return parent.children.some(child => {
      if (this.isDropdown(child)) {
        return this.hasActiveChilds(child);
      }

      if (this.isLink(child) && !this.isFunction(child.route)) {
        return this.router.isActive(child.route as string, false);
      }
    });
  }

  isFunction(prop: NavigationLink['route']) {
    return prop instanceof Function;
  }

  makeAction(name: string) {
    switch (name) {
      case 'addJob':
        this.canAddJobs();
        break;
      case 'addJobApproval':
        this.openJobApprovalDialog();
        break;
      case 'accountSetup':
        this.showTutorial(Tutorials.AccountSetup)
        break;
      case 'setupCareerPage':
        this.showTutorial(Tutorials.SetupCareerPage)
        break;
      case 'jobCreation':
        this.showTutorial(Tutorials.JobCreation)
        break;
      case 'manageCandidateProfile':
        this.showTutorial(Tutorials.ManageCandidateProfile)
        break;
      case 'emailTemplate':
        this.showTutorial(Tutorials.EmailTemplate)
        break;
      case 'customFields':
        this.showTutorial(Tutorials.CustomFields)
        break;
      case 'prescreenQuestions':
        this.showTutorial(Tutorials.PrescreenQuestions)
        break;
      case 'jobTemplate':
        this.showTutorial(Tutorials.JobTemplate)
        break;
      default:
        break;
    }
  }

  canAddJobs() {
    const packages = JSON.parse(localStorage.getItem(PreferenceKeys.MembershipPackages));
    
    // check for membership
    const membership = localStorage.getItem('membership');
    let subscribedTier = packages.find(tier => tier.id == membership);

    // it should be always here but to make previous logic working here
    if (!subscribedTier) {
      const monthlyProducts = packages.filter(product => product.length == 1)
        .sort((lhs, rhs) => lhs.price - rhs.price);
      
      if (monthlyProducts.length > 0) {
        subscribedTier = monthlyProducts[0];
      }
    }

    if (subscribedTier) {
      const $totalJobsCount = +localStorage.getItem(PreferenceKeys.TotalJobsCount);
      if (subscribedTier.jobs && $totalJobsCount >= subscribedTier.jobs) {
        const alert = this.dialog.open(ConfirmDialogComponent, {
          position: { top: '0px'},
          data: {
            title: this.translate.instant('MEMBERSHIPS.UPGRADE_YOUR_MEMBERSHIP'),
            message: this.translate.instant('MEMBERSHIPS.JOBS_LIMITED'), 
            positiveTitle: this.translate.instant('MEMBERSHIPS.UPGRADE_NOW')
          }
        });

        alert.afterClosed().subscribe(result => {
          if (result===true) {
            this.upgradeMembership();
          }
        });

        return;
      }
    }

    // const membershipIndex = +localStorage.getItem('membership');
    // if (membershipIndex < packages.length) {
    //   const current = packages[membershipIndex];

    //   if (current.jobs && $totalJobsCount >= current.jobs) {
    //     const alert = this.dialog.open(ConfirmDialogComponent, {
    //       position: { top: '0px'},
    //       data: {
    //         title: this.translate.instant('MEMBERSHIPS.UPGRADE_YOUR_MEMBERSHIP'),
    //         message: this.translate.instant('MEMBERSHIPS.JOBS_LIMITED'), 
    //         positiveTitle: this.translate.instant('MEMBERSHIPS.UPGRADE_NOW')
    //       }
    //     });

    //     alert.afterClosed().subscribe(result => {
    //       if (result===true) {
    //         this.upgradeMembership();
    //       }
    //     });

    //     return;
    //   }
    // }

    this.dialog
      .open(JobCreateUpdateComponent, {
        width: '100%',
        disableClose: true
      }).afterClosed()
      .subscribe((created) => {
        if (created) {
          this.jobService.didCreateNewJob();
        }
      });
  }

  upgradeMembership() {
    this.dialog.open(SubscriptionComponent, {
      width: '100%', 
      autoFocus: false,
      disableClose: true
    }).afterClosed().subscribe(updated => {

    })
  }

  openJobApprovalDialog() {
    this.dialog
    .open(JobApprovalCreateUpdateComponent, {
      width: '100%',
      disableClose: true
    });
  }

  showTutorial(url: Tutorials) {
    this.dialog
      .open(VideoTutorialComponent, {
        data: {
          'url': url
        },
        disableClose: false,
        panelClass: 'tutorial-container',
      });
  }
}
